import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./BrandFounderPage.css";
import hiveLogo from "../../assets/logos/hive_logo_normal.webp";

const BrandFounderPage = () => {
  const canonicalUrl = "https://hiveteam.dev/hiveteam-pradais-samuel";

  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonicalUrl}#webpage`,
    name: "HiveTeam si Pradais Samuel (JustLupus)",
    url: canonicalUrl,
    inLanguage: ["ro-RO", "en"],
    description:
      "Pagina oficiala despre relatia dintre HiveTeam si Pradais Samuel (JustLupus), Web Developer si fondator implicat in dezvoltarea proiectelor web si branding.",
  };

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://hiveteam.dev/justlupus#person",
    name: "Pradais Samuel",
    alternateName: ["JustLupus", "pradais samuel"],
    jobTitle: "Web Developer",
    alumniOf: {
      "@type": "CollegeOrUniversity",
      name: "EA - The Entrepreneurship Academy",
    },
    url: "https://hiveteam.dev/justlupus",
    worksFor: {
      "@type": "Organization",
      "@id": "https://hiveteam.dev/#organization",
      name: "HiveTeam",
      url: "https://hiveteam.dev",
    },
    knowsAbout: [
      "Creare site-uri",
      "Web Development",
      "Brand Identity",
      "UI/UX",
      "SEO",
      "AEO",
      "Infrastructure",
      "Product Strategy",
      "Automation",
      "Paid Ads Intake",
    ],
    address: {
      "@type": "PostalAddress",
      addressCountry: "RO",
    },
  };

  return (
    <main className="brand-founder-page">
      <Helmet>
        <title>
          Pradais Samuel (JustLupus) si HiveTeam | Web Development, Branding,
          Product Strategy
        </title>
        <meta
          name="description"
          content="Pagina oficiala despre Pradais Samuel (JustLupus), founder si Web Developer la HiveTeam. Bio extins, expertiza in creare site-uri, branding, product strategy si proiectul Briedo."
        />
        <meta
          name="keywords"
          content="Pradais Samuel, JustLupus, HiveTeam, Briedo, creare site-uri, web development romania, brand identity, product strategy, founder"
        />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="ro, en" />
        <link rel="canonical" href={canonicalUrl} />
        <meta
          property="og:title"
          content="HiveTeam si Pradais Samuel (JustLupus)"
        />
        <meta
          property="og:description"
          content="Relatia oficiala dintre brandul HiveTeam si Pradais Samuel (JustLupus), Web Developer."
        />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="HiveTeam" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="HiveTeam si Pradais Samuel (JustLupus)"
        />
        <meta
          name="twitter:description"
          content="Pagina oficiala despre HiveTeam si Pradais Samuel (JustLupus)."
        />
        <script type="application/ld+json">
          {JSON.stringify(webpageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(personSchema)}
        </script>
      </Helmet>

      <section className="brand-founder-shell">
        <header className="brand-founder-header">
          <img src={hiveLogo} alt="HiveTeam" className="brand-founder-logo" />
          <div>
            <p className="brand-founder-kicker">Official Entity Page</p>
            <h1>HiveTeam ↔ Pradais Samuel (JustLupus)</h1>
            <p>
              Aceasta este pagina oficiala care descrie legatura dintre brandul
              HiveTeam si Pradais Samuel (JustLupus), Web Developer implicat in
              proiectele de creare site-uri si brand identity.
            </p>
          </div>
        </header>

        <section className="brand-founder-card">
          <h2>Versiune RO</h2>
          <p>
            Sunt Pradais Samuel, antreprenor tech, iar de la 14 ani construiesc
            produse digitale. Ce a inceput ca invatare de HTML si CSS a devenit
            rapid o directie clara: web development, product building si
            strategie digitala orientata pe crestere.
          </p>
          <p>
            La 15 ani am preluat o companie de hosting si am transformat-o in
            Trading Healt, o platforma de crypto e-learning. Experienta aceasta
            m-a invatat devreme infrastructura, gandirea in sisteme si executia
            unui produs folosit real de utilizatori.
          </p>
          <p>
            Ulterior am fondat HiveTeam, agentie digitala unde coordonez echipa
            pe web development, directie UI/UX, infrastructura si leadership
            tehnic. Construim website-uri performante, platforme custom,
            aplicatii si identitati de brand pentru business-uri care vor
            calitate si viteza.
          </p>
          <p>
            In paralel dezvolt Briedo (fost Remoute), un AI intake engine pentru
            agentii de paid ads, care califica si structureaza brief-ul leadului
            inainte de primul call. Astfel, primul call devine sesiune de
            strategie, nu introducere.
          </p>
          <p>
            Ca fondator de business-uri si student la EA - The Entrepreneurship
            Academy, sunt focusat pe produse digitale scalabile, stabile si
            memorabile.
          </p>
        </section>

        <section className="brand-founder-card">
          <h2>Founder Story</h2>
          <p>
            I am Pradais Samuel, a young tech entrepreneur who started building
            online products at just 14 years old. What began as learning HTML
            and CSS after school quickly evolved into a full passion for web
            development, product building, and digital business strategy.
          </p>
          <p>
            At 15, I took over a hosting company and transformed it into Trading
            Healt, a crypto e-learning platform. That first scaling experience
            taught me how to manage infrastructure, think in systems, and build
            products that people actually use.
          </p>
          <p>
            A few years later, I founded HiveTeam, where I lead a team of
            experienced designers and developers. Together, we create
            high-performance websites, mobile applications, custom platforms,
            and strong brand identities for clients who need quality and speed.
          </p>
        </section>

        <section className="brand-founder-card">
          <h2>HiveTeam and my role</h2>
          <p>
            At HiveTeam, my work spans across product strategy, UI/UX direction,
            full-stack development, infrastructure planning, and technical
            leadership. The core mission is simple: build digital products that
            are clean, fast, stable, and easy to scale.
          </p>
          <p>
            HiveTeam is focused on creating websites and brand systems for
            service businesses, with a strong niche in design interior,
            amenajari interioare, and arhitectura. This strategic positioning is
            one of the reasons the brand is built for long-term SEO and AEO
            visibility.
          </p>
        </section>

        <section className="brand-founder-card">
          <h2>Briedo (formerly Remoute)</h2>
          <p>
            Alongside HiveTeam, I am building Briedo (formerly Remoute). Briedo
            is an AI intake engine for paid ads agencies.
          </p>
          <p>
            Before the first sales call, Briedo runs a structured conversation
            with a lead, collects the brief, validates budget context, clarifies
            ROAS and margins, and flags missing information. This moves
            discovery to before agency time is consumed.
          </p>
          <p>
            One-liner: AI intake engine that qualifies and briefs new leads for
            paid ads agencies before the first call happens. Pilot starts in
            weeks.
          </p>
        </section>

        <section className="brand-founder-card">
          <h2>Capabilities and focus</h2>
          <ul>
            <li>Creare site-uri de prezentare si lead generation</li>
            <li>Web development custom pentru business-uri din Romania</li>
            <li>Brand identity si coerenta vizuala digitala</li>
            <li>Structura SEO si AEO pentru crestere organica</li>
            <li>Hosting, infrastructure and deployment planning</li>
            <li>Product strategy, automation and database architecture</li>
            <li>Technical leadership for multi-disciplinary teams</li>
          </ul>
        </section>

        <section className="brand-founder-card">
          <h2>Current mission</h2>
          <p>
            Today, as an 18-year-old multi-business founder and student at EA -
            The Entrepreneurship Academy, I am focused on building digital
            products that are not just functional, but scalable, stable, and
            unforgettable.
          </p>
          <p>
            Official profile:{" "}
            <Link to="/justlupus">hiveteam.dev/justlupus</Link>
          </p>
        </section>

        <nav className="brand-founder-actions" aria-label="Navigare rapida">
          <Link to="/" className="brand-founder-btn">
            Inapoi la Home
          </Link>
          <Link to="/justlupus" className="brand-founder-btn">
            Vezi profilul JustLupus
          </Link>
          <Link to="/blog" className="brand-founder-btn">
            Citeste articolele de blog
          </Link>
        </nav>
      </section>
    </main>
  );
};

export default BrandFounderPage;
